import { IntlShape } from 'react-intl';
import { Music24Capabilities, Music24PlanTier, music24PlanCapabilities } from 'fym-common/src/license/music24Plans';
import {
  MUSIC24_FEATURE_GROUPS,
  Music24FeatureRow,
  featureRowIncludedText,
  featureRowNoneText,
  featureRowsInGroup,
} from '../config/music24PlanFeatureRows';

/** The tiers of the plans page, in the order of the cards. Plug is not sold here. */
export type PlansPageTier = Exclude<Music24PlanTier, 'trial'>;

/**
 * The plan that each card says "Everything in X, plus" of (design plans-page a). A card lists only
 * the rows that differ from that plan. Plug, when a workspace is still on it, builds on Pro.
 */
export const PLAN_BUILDS_ON: Record<Exclude<PlansPageTier, 'free'>, PlansPageTier> = {
  artist: 'free',
  pro: 'artist',
  plug: 'pro',
  manager: 'pro',
  label: 'manager',
};

/** The rows the Free row shows: what Free gives, so the step up reads clearly. */
const FREE_ROW_KEYS = ['pages', 'releases', 'socialAccounts', 'scheduledPosts', 'videoCredits', 'history'];

export interface PlanHighlight {
  key: string;
  label: string;
  /** `null` when the row is a plain "Included": the label says it all. */
  value: string | null;
}

function highlightOf(row: Music24FeatureRow, capabilities: Music24Capabilities, intl: IntlShape): PlanHighlight {
  const value = row.value(capabilities, intl);
  return {
    key: row.key,
    label: intl.formatMessage(row.label),
    value: value === featureRowIncludedText(intl) ? null : value,
  };
}

function allRows(): Music24FeatureRow[] {
  return MUSIC24_FEATURE_GROUPS.flatMap((group) => featureRowsInGroup(group));
}

/**
 * The lines of one plan card. Free: the fixed rows of `FREE_ROW_KEYS`. A paid plan: every row
 * whose value differs from the plan it builds on and is not "none", in the order of the compare
 * table. The card shows the first few and links the rest to the table.
 */
export function planHighlights(tier: PlansPageTier, intl: IntlShape): PlanHighlight[] {
  const rows = allRows();
  const capabilities = music24PlanCapabilities[tier];
  if (tier === 'free') {
    return FREE_ROW_KEYS.flatMap((key) => {
      const row = rows.find((candidate) => candidate.key === key);
      return row === undefined ? [] : [highlightOf(row, capabilities, intl)];
    });
  }
  const base = music24PlanCapabilities[PLAN_BUILDS_ON[tier]];
  const none = featureRowNoneText(intl);
  return rows
    .filter((row) => {
      const value = row.value(capabilities, intl);
      return value !== none && value !== row.value(base, intl);
    })
    .map((row) => highlightOf(row, capabilities, intl));
}
